import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SampleNote from '../components/SampleNote';
import { sampleReviewNote } from '../data/examples';
import '../styles/home.css';

const fieldClass =
  'w-full bg-white border border-[#EAF1EC] rounded-xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#174C3C]/20 text-lg font-medium';
const labelClass = 'text-sm font-black text-[#17211E] uppercase tracking-widest';

const DigitalReview = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Digital Review | ManagedSite.net';
    const description =
      'Send the website. Dave looks at the site, email, and hosting, then replies with what is going on.';
    document.title = title;
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());
    payload.audience = 'business';
    payload.help_type = 'Review';

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'The message could not be sent.');
      }

      setSubmitted(true);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'contact',
          event_label: 'review',
        });
      }
    } catch (error) {
      setSubmitError(error.message || 'The message could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full min-h-[70vh] flex items-center justify-center bg-[#FAF8F3] px-4">
        <div className="max-w-2xl w-full bg-white p-12 md:p-20 rounded-[3rem] border border-[#EAF1EC] shadow-xl text-center">
          <div className="w-20 h-20 bg-[#EAF1EC] text-[#174C3C] rounded-full flex items-center justify-center mx-auto mb-10">
            <SafeIcon icon={FiCheck} className="w-10 h-10" />
          </div>
          <h2 className="text-4xl font-extrabold text-[#17211E] mb-6">You will hear from Dave</h2>
          <p className="text-xl text-[#17211E]/75 font-medium leading-relaxed mb-12">
            Most reviews get a reply within one business day.
          </p>
          <Link to="/" className="home-button">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="bg-[#FAF8F3] pt-24 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Digital Review</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl">
            Send the website. We&apos;ll tell you what is going on.
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed max-w-3xl font-medium">
            Dave looks at the site, email, and hosting, then writes back. No logins needed to start.
          </p>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-16 lg:grid-cols-[1.1fr_.9fr]">
          <div>
            <h2 className="text-4xl font-bold text-[#17211E] mb-10">What happens</h2>
            <ul className="space-y-8 text-lg leading-relaxed text-[#17211E]/75">
              <li>
                <span className="block font-bold text-[#17211E] mb-2">We look at the setup</span>
                Website, business email, hosting, and the obvious risks.
              </li>
              <li>
                <span className="block font-bold text-[#17211E] mb-2">Dave replies</span>
                What is going on, what can wait, and whether this is a one-time fix or monthly care.
              </li>
              <li>
                <span className="block font-bold text-[#17211E] mb-2">You decide the next step</span>
                If monthly care is the fit, we quote Essential, Managed, or Advanced Care — the same plans agencies use. If we are not a fit, you still have the note.
              </li>
            </ul>
            <p className="mt-12 text-lg text-[#17211E]/60 font-medium">
              Already know you want monthly care?{' '}
              <Link to="/get-started" className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4">
                Get started
              </Link>
              . Need something else?{' '}
              <Link to="/contact" className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4">
                Use the contact form
              </Link>
              .
            </p>
            <div className="mt-14">
              <SampleNote note={sampleReviewNote} />
            </div>
          </div>

          <div className="bg-[#FAF8F3] rounded-[2.5rem] border border-[#EAF1EC] p-8 sm:p-12">
            <h2 className="text-3xl font-extrabold text-[#17211E] mb-8">Request a Digital Review</h2>
            <form method="POST" onSubmit={handleSubmit} className="space-y-8">
              <input type="hidden" name="audience" value="business" />
              <input type="hidden" name="help_type" value="Review" />
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="review-company-fax">Company fax</label>
                <input id="review-company-fax" type="text" name="company_fax" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Full Name *</label>
                <input type="text" name="name" required className={fieldClass} placeholder="Your name" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Email Address *</label>
                <input type="email" name="email" required className={fieldClass} placeholder="you@company.com" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Company name *</label>
                <input type="text" name="company" required className={fieldClass} placeholder="Your company" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Website URL *</label>
                <input type="url" name="url" required className={fieldClass} placeholder="https://" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>What is going on? *</label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  className={fieldClass}
                  placeholder="A short note is enough. What is worrying you?"
                />
              </div>

              <div>
                {submitError && (
                  <p className="mb-6 text-center text-lg font-bold text-[#8A2B2B]">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="home-button w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Request a Digital Review'}
                </button>
                <p className="text-center mt-6 text-[#17211E]/50 font-bold italic">
                  Dave usually replies within one business day.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-[#EAF1EC] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center sm:px-6">
          <p className="text-lg text-[#17211E]/75 font-medium leading-relaxed">
            Agencies can request the same look at a client site.{' '}
            <Link
              to="/contact?audience=agency"
              className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4"
            >
              Use the agency form
            </Link>
            {' '}if you need white-label delivery.
          </p>
        </div>
      </section>
    </div>
  );
};

export default DigitalReview;
